import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppContext } from 'context/AppContext';
import strings from 'localization/strings';
import { updateActorPaymentSetup, sanitizeSetupFormValues, createSetupFormInitialValues, stripeFormIsIncomplete, handleSetupCompleted } from 'logic/paymentLogic';
import { showSuccessDialog, showErrorDialog } from 'actions/responses';
import { enhanceStripeErrorMessage } from 'helpers/StripeHelper';
import paymentProviders from 'enums/paymentProviders';
import queryString from 'query-string';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import PaymentElement from 'components/payment/PaymentElement';
import MarginBox from 'common/MarginBox';
import LoadingWrapper from 'form/LoadingWrapper';
import Form from 'form/Form';
import ButtonContainer from 'common/ButtonContainer';
import StripeElementsWrapper from 'components/stripe/StripeElementsWrapper';

const StripePaymentMethodSettings = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const { appContext } = useAppContext();

    const authenticationContext = useSelector(state => state.authentication.context);
    const { selectedActor } = authenticationContext;

    const [ isSaving, setIsSaving ] = useState(false);
    const [showPaymentProviderSelector, setShowPaymentProviderSelector] = useState(true);
    const querystringParameters = queryString.parse(location.search);
    const setupCompleted = querystringParameters.setupCompleted === 'true';

    useEffect(() => {
        if(setupCompleted) {
            handleSetupCompleted({ history });
            dispatch(showSuccessDialog({ title: strings.updatePaymentSetupSucceededTitle, body: strings.updatePaymentSetupSucceededBody }));
        }
    }, [setupCompleted]);

    const initialValues = {
        setup: createSetupFormInitialValues({
            paymentProvider: paymentProviders.stripe.key,
            stripe: {}
        }, appContext)
    };

    const handleFormSubmit = values => {
        setIsSaving(true);
        updateActorPaymentSetup({
            setup: values.setup,
            dispatch,
            actor: selectedActor,
            onFailure: response => {
                setIsSaving(false);
                const failureContent = { title: strings.updatePaymentSetupFailedTitle, body: strings.updatePaymentSetupFailedBody };
                dispatch(showErrorDialog(enhanceStripeErrorMessage(response), failureContent));
            }
        });
    };

    const handleShowPaymentProviderSelector = value => {
        setShowPaymentProviderSelector(value);
    };

    return (
        <LoadingWrapper
            isSaving={isSaving}
        >
            <StripeElementsWrapper setupActorId={selectedActor.id}>
                {
                    ({ stripeSetupContext }) => (
                        <Form
                            initialValues={initialValues}
                            onSubmit={values => handleFormSubmit(sanitizeSetupFormValues(values, stripeSetupContext))}
                        >
                            {({ values, handleSubmit, invalid }) => (
                                <form onSubmit={handleSubmit}>
                                    {
                                        stripeSetupContext?.savedPaymentMethods?.length > 0 &&
                                        (
                                            <>
                                                <Box>
                                                    {strings.currentPaymentMethod}: <strong>{strings.formatString(strings.protectedCardDetails, stripeSetupContext.savedPaymentMethods[0].last4, stripeSetupContext.savedPaymentMethods[0].expirationMonth, stripeSetupContext.savedPaymentMethods[0].expirationYear)}</strong>
                                                </Box>
                                                {
                                                    stripeSetupContext.hasMultipleActivePaymentMethods &&
                                                    (
                                                        <MarginBox top={4}>
                                                            {strings.otherPaymentMethodsInUse}
                                                        </MarginBox>
                                                    )
                                                }
                                            </>
                                        )
                                    }
                                    {
                                        !(stripeSetupContext?.savedPaymentMethods?.length > 0) &&
                                        (
                                            <Box>
                                                {strings.noPaymentMethod}
                                            </Box>
                                        )
                                    }

                                    <MarginBox top={4} bottom={4}>
                                        <PaymentElement
                                            name="setup"
                                            paymentProviders={[paymentProviders.stripe.key]}
                                            stripeSetupContext={stripeSetupContext}
                                            showPaymentProviderSelector={showPaymentProviderSelector}
                                            changeIntended
                                        />
                                    </MarginBox>

                                    <ButtonContainer>
                                        {
                                            !showPaymentProviderSelector &&
                                            (
                                                <>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        color="primary"
                                                        disabled={invalid || stripeFormIsIncomplete(values.setup, stripeSetupContext)}
                                                    >
                                                        {strings.save}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={() => handleShowPaymentProviderSelector(true)}
                                                    >
                                                        {strings.cancel}
                                                    </Button>
                                                </>
                                            )
                                        }
                                        {
                                            showPaymentProviderSelector &&
                                            (
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleShowPaymentProviderSelector(false)}
                                                >
                                                    {stripeSetupContext?.savedPaymentMethods?.length > 0 ? strings.updatePaymentMethod : strings.addPaymentMethod}
                                                </Button>
                                            )
                                        }
                                    </ButtonContainer>
                                </form>
                            )}
                        </Form>
                    )
                }
            </StripeElementsWrapper>
        </LoadingWrapper>
    );
};

export default StripePaymentMethodSettings;
